import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { StatusHistoryPill } from '@/components/StatusHistoryPill'
import { getAllowedCalloutStatusTransitions } from '@/db/domain/calloutStatus'
import type { SessionSubtype, StatusChangeLog } from '@/lib/schemas/db'

export type CalloutStatus =
  | 'New'
  | 'Booked'
  | 'LeftSite'
  | 'Arrived'
  | 'Review'
  | 'ReadyToInvoice'
  | 'Invoiced'
  | 'Cancelled'

export type ServiceType = 'Callout' | 'Session'

export interface BookingRow {
  /** Public-facing ID shown in UI and URLs */
  id: string
  /** Private/internal DB id for mutations */
  privateId?: string
  /** Display date (YYYY-MM-DD) derived from startTime */
  date: string
  /** Display time label derived from startTime/wholeDay */
  time: string
  /** ISO start time for the booking (e.g. 2025-01-10T09:00:00) */
  startTime?: string
  /** ISO end time for the booking */
  endTime?: string
  /** When true, this is an all-day booking and specific times are less important */
  wholeDay?: boolean
  customerName: string
  customerId?: string
  customerPublicId?: string
  issueSummary: string
  serviceType: ServiceType
  sessionSubtype?: SessionSubtype
  calloutStatus?: CalloutStatus
  technicianName?: string
  contactAddress?: string
  contactPhone?: string
  contactEmail?: string
  invoiceName?: string
  invoiceVat?: string
  invoiceAddress?: string
  invoiceEmail?: string
  statusChanges?: StatusChangeLog[]
}

export type BookingCardVariant = 'header' | 'body' | 'collapsible'

export interface BookingCardProps {
  booking: BookingRow
  variant?: BookingCardVariant
  defaultExpanded?: boolean
  onModifyCallout?: (booking: BookingRow) => void
  onMarkReadyToInvoice?: (booking: BookingRow) => void
  onCancelCallout?: (booking: BookingRow) => void
  getAuthorDisplayName?: (authorId: string) => string
}

function BookingCardHeader(props: {
  booking: BookingRow
  chevronDirection: 'up' | 'down'
  getAuthorDisplayName?: (authorId: string) => string
}) {
  const { booking, chevronDirection, getAuthorDisplayName } = props
  const status = booking.calloutStatus ?? booking.serviceType

  return (
    <div className="flex items-center justify-between px-3 py-2 text-xs">
      <div className="flex min-w-0 items-center gap-3">
        <div className="whitespace-nowrap text-[11px] font-medium text-slate-500">
          {booking.date} • {booking.time}
        </div>
        {booking.customerId ? (
          <Link
            to="/customers/$id"
            params={{ id: booking.customerPublicId ?? booking.customerId }}
            className="truncate text-sm font-medium text-blue-700 hover:underline"
          >
            {booking.customerName}
          </Link>
        ) : (
          <span className="truncate text-sm font-medium text-slate-800">
            {booking.customerName}
          </span>
        )}
      </div>

      <div className="ml-3 flex items-center gap-2">
        <StatusHistoryPill
          currentStatus={status as CalloutStatus}
          statusChanges={booking.statusChanges}
          getAuthorDisplayName={getAuthorDisplayName}
          dropdownStrategy="absolute"
        />
        {chevronDirection === 'up' ? (
          <ChevronUp
            className="h-3 w-3 text-slate-400"
            aria-hidden="true"
          />
        ) : (
          <ChevronDown
            className="h-3 w-3 text-slate-400"
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  )
}

function BookingCardBody({
  booking,
  showCustomerName,
  onModifyCallout,
  onMarkReadyToInvoice,
  onCancelCallout,
  getAuthorDisplayName,
}: {
  booking: BookingRow
  showCustomerName: boolean
  onModifyCallout?: (booking: BookingRow) => void
  onMarkReadyToInvoice?: (booking: BookingRow) => void
  onCancelCallout?: (booking: BookingRow) => void
  getAuthorDisplayName?: (authorId: string) => string
}) {
  const status = booking.calloutStatus
  const isClosed = status === 'Invoiced' || status === 'Cancelled'
  const isReadyToInvoice = status === 'ReadyToInvoice'
  const isSession = booking.serviceType === 'Session'
  const isCallout = booking.serviceType === 'Callout'
  const isInvoiced = booking.calloutStatus === 'Invoiced'
  const sessionSubtypeLabel = booking.sessionSubtype ?? 'TeamViewer Session'
  const detailsHref = isSession
    ? `/sessions/${booking.id}`
    : `/callouts/${booking.id}`
  const prepInvoiceHref = isSession
    ? `/sessions/${booking.id}/invoice`
    : `/callouts/${booking.id}/invoice`
  const canMoveToReadyToInvoice =
    Boolean(status) &&
    getAllowedCalloutStatusTransitions(status as CalloutStatus).includes('ReadyToInvoice')

  return (
    <div className="p-4">
      <div className="space-y-2">
        {showCustomerName ? (
          <div className="text-sm">
            {booking.customerId ? (
              <Link
                to="/customers/$id"
                params={{ id: booking.customerPublicId ?? booking.customerId }}
                className="font-medium text-blue-700 hover:underline"
              >
                {booking.customerName}
              </Link>
            ) : (
              <span className="font-medium text-slate-800">{booking.customerName}</span>
            )}
          </div>
        ) : null}
        <div className="text-sm text-slate-800">
          {booking.issueSummary}
        </div>
        {booking.contactAddress ? (
          <div className="text-xs text-slate-600">
            {booking.contactAddress}
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2 text-xs text-slate-600">
          <span
            className={
              isInvoiced
                ? 'inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-slate-700'
                : booking.serviceType === 'Session'
                  ? sessionSubtypeLabel === 'Sit-down Session'
                    ? 'inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700'
                    : 'inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-blue-700'
                  : 'inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-purple-700'
            }
          >
            {booking.serviceType === 'Session' ? sessionSubtypeLabel : booking.serviceType}
          </span>
          {booking.calloutStatus ? (
            <StatusHistoryPill
              currentStatus={booking.calloutStatus}
              statusChanges={booking.statusChanges}
              getAuthorDisplayName={getAuthorDisplayName}
              dropdownStrategy="absolute"
            />
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          {!isClosed ? (
            <button
              type="button"
              className="rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
              onClick={() => onModifyCallout?.(booking)}
            >
              Modify
            </button>
          ) : null}
          {(isCallout || isSession) && !isClosed && !isReadyToInvoice ? (
            isCallout ? (
              <Link
                to="/callouts/$id/logs"
                params={{ id: booking.id }}
                className="inline-flex items-center rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
              >
                Log work
              </Link>
            ) : (
              <a
                href={`/sessions/${booking.id}/logs`}
                className="inline-flex items-center rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
              >
                Log work
              </a>
            )
          ) : null}
          {(isCallout || isSession) && !isClosed ? (
            <button
              type="button"
              className="rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
              onClick={() => onCancelCallout?.(booking)}
            >
              {isSession ? 'Cancel Session' : 'Cancel Callout'}
            </button>
          ) : null}
          {booking.serviceType === 'Callout' && canMoveToReadyToInvoice ? (
            <button
              type="button"
              className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
              onClick={() => onMarkReadyToInvoice?.(booking)}
            >
              Mark Ready to Invoice
            </button>
          ) : null}
          {isReadyToInvoice ? (
            <a
              href={prepInvoiceHref}
              className="inline-flex items-center px-1 py-1 text-xs font-medium text-blue-700 underline underline-offset-2 hover:text-blue-800"
            >
              Prep Invoice
            </a>
          ) : null}
          {isClosed ? (
            <a
              href={detailsHref}
              className="inline-flex items-center px-1 py-1 text-xs font-medium text-blue-700 underline underline-offset-2 hover:text-blue-800"
            >
              {isSession ? 'View Session' : 'View Callout'}
            </a>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export function BookingCard({
  booking,
  variant = 'body',
  defaultExpanded = false,
  onModifyCallout,
  onMarkReadyToInvoice,
  onCancelCallout,
  getAuthorDisplayName,
}: BookingCardProps) {
  if (variant === 'header') {
    return (
      <div className="rounded-md border border-slate-200 bg-white">
        <BookingCardHeader
          booking={booking}
          chevronDirection="down"
          getAuthorDisplayName={getAuthorDisplayName}
        />
      </div>
    )
  }

  if (variant === 'collapsible') {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded)

    return (
      <div className="rounded-md border border-slate-200 bg-white">
        <button
          type="button"
          className="w-full text-left border-b border-slate-100"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
        >
          <BookingCardHeader
            booking={booking}
            chevronDirection={isExpanded ? 'up' : 'down'}
            getAuthorDisplayName={getAuthorDisplayName}
          />
        </button>
        {isExpanded ? (
          <BookingCardBody
            booking={booking}
            showCustomerName={false}
            onModifyCallout={onModifyCallout}
            onMarkReadyToInvoice={onMarkReadyToInvoice}
            onCancelCallout={onCancelCallout}
            getAuthorDisplayName={getAuthorDisplayName}
          />
        ) : null}
      </div>
    )
  }

  // body-only card
  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <BookingCardBody
        booking={booking}
        showCustomerName
        onModifyCallout={onModifyCallout}
        onMarkReadyToInvoice={onMarkReadyToInvoice}
        onCancelCallout={onCancelCallout}
        getAuthorDisplayName={getAuthorDisplayName}
      />
    </div>
  )
}
